const MASKED_ATTRIBUTE_VALUE = '***'
const UNKNOWN_ELEMENT = '<unknown>'
const MAX_ELEMENT_DEPTH = 5
const MAX_LABEL_LENGTH = 80
const ATTRIBUTE_START = /\[[\w-]+="/
const SENSITIVE_ATTRIBUTES = ['title', 'aria-label', 'alt', 'placeholder']
const ELEMENT_LABELLED_SPAN_OPS = ['ui.interaction.', 'ui.webvital.']

type ElementLike = {
  tagName: string
  id?: unknown
  className?: unknown
  parentElement?: unknown
}

type BreadcrumbLike = {
  category?: string
  message?: string
  data?: Record<string, unknown>
}

type SpanLike = {
  op?: string
  description?: string
  data: Record<string, unknown>
}

export const sentryMaskedAttributes = SENSITIVE_ATTRIBUTES.concat('name')

// Sentry 11 collects request and response bodies, GraphQL variables, database and queue
// payloads, cookies and stack frame locals unless told otherwise. Any of them can carry report
// content, key material or a reporter's identity, which must never leave our servers.
export const sentryDataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
}

export const maskSensitiveAttribute = (name: string, value: string) =>
  SENSITIVE_ATTRIBUTES.includes(name) ? MASKED_ATTRIBUTE_VALUE : value

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isElementLike = (value: unknown): value is ElementLike =>
  isRecord(value) && typeof value['tagName'] === 'string'

const describeNode = ({ tagName, id, className }: ElementLike) => {
  const idSelector = typeof id === 'string' && id !== '' ? `#${id}` : ''
  const classSelectors =
    typeof className === 'string'
      ? className
          .split(/\s+/)
          .filter(Boolean)
          .map(name => `.${name}`)
          .join('')
      : ''
  return `${tagName.toLowerCase()}${idSelector}${classSelectors}`
}

const describeElement = (
  element: ElementLike,
  label = describeNode(element),
  depth = 1
): string => {
  const parent = element.parentElement
  if (
    depth >= MAX_ELEMENT_DEPTH ||
    !isElementLike(parent) ||
    parent.tagName.toLowerCase() === 'html'
  ) {
    return label
  }
  const extendedLabel = `${describeNode(parent)} > ${label}`
  return extendedLabel.length >= MAX_LABEL_LENGTH
    ? label
    : describeElement(parent, extendedLabel, depth + 1)
}

const describeElementValue = (element: ElementLike) => `[HTMLElement: ${describeElement(element)}]`

export const maskBreadcrumb = <T extends BreadcrumbLike>(
  breadcrumb: T,
  hint?: { event?: unknown; level?: unknown }
): T => {
  if (breadcrumb.category === 'console') {
    return maskConsoleBreadcrumb(breadcrumb, hint?.level)
  }
  if (!breadcrumb.category?.startsWith('ui.') || breadcrumb.message === undefined) {
    return breadcrumb
  }
  const element = breadcrumbElement(hint?.event)
  return {
    ...breadcrumb,
    message: isElementLike(element) ? describeElement(element) : UNKNOWN_ELEMENT,
  }
}

const maskConsoleBreadcrumb = <T extends BreadcrumbLike>(breadcrumb: T, level: unknown): T => {
  const args = breadcrumb.data?.['arguments']
  if (!Array.isArray(args) || !args.some(isElementLike)) {
    return breadcrumb
  }
  const message = args.map(describeConsoleArgument).join(' ')
  return {
    ...breadcrumb,
    message: level === 'assert' ? `Assertion failed: ${message}` : message,
    data: {
      ...breadcrumb.data,
      arguments: args.map(argument =>
        isElementLike(argument) ? describeElementValue(argument) : argument
      ),
    },
  }
}

const describeConsoleArgument = (argument: unknown) => {
  if (isElementLike(argument)) {
    return describeElementValue(argument)
  }
  if (argument instanceof Error) {
    return argument.message ? `${argument.name}: ${argument.message}` : argument.name
  }
  return isRecord(argument) || typeof argument === 'function'
    ? Object.prototype.toString.call(argument)
    : String(argument)
}

const breadcrumbElement = (event: unknown) =>
  isElementLike(event) || !isRecord(event) ? event : event['target']

export const maskRecordingEvent = <T extends { data: unknown }>(event: T): T => {
  const { data } = event
  if (!isRecord(data)) {
    return event
  }
  const { tag, payload } = data
  if (tag !== 'breadcrumb' || !isRecord(payload)) {
    return event
  }
  const { category, message } = payload
  if (typeof category !== 'string' || !category.startsWith('ui.') || message === undefined) {
    return event
  }
  return {
    ...event,
    data: { ...data, payload: { ...payload, message: describeRecordedNode(payload['data']) } },
  }
}

const describeRecordedNode = (breadcrumbData: unknown) => {
  const node = isRecord(breadcrumbData) ? breadcrumbData['node'] : undefined
  if (!isRecord(node)) {
    return UNKNOWN_ELEMENT
  }
  const { tagName, attributes } = node
  if (typeof tagName !== 'string') {
    return UNKNOWN_ELEMENT
  }
  const { id, class: className } = isRecord(attributes) ? attributes : {}
  return describeNode({ tagName, id, className })
}

export const maskEventTarget = <T extends { extra?: Record<string, unknown> }>(
  event: T,
  hint: { originalException?: unknown }
): T => {
  const { originalException } = hint
  const serialized = event.extra?.['__serialized__']
  if (!isRecord(originalException) || !isRecord(serialized)) {
    return event
  }
  const { target, currentTarget } = originalException
  if (!isElementLike(target)) {
    return event
  }
  return {
    ...event,
    extra: {
      ...event.extra,
      __serialized__: {
        ...serialized,
        target: describeElementValue(target),
        currentTarget: isElementLike(currentTarget)
          ? describeElementValue(currentTarget)
          : serialized['currentTarget'],
      },
    },
  }
}

const stripElementAttributes = (label: string) => {
  const attributesStart = label.search(ATTRIBUTE_START)
  return attributesStart === -1 ? label : label.slice(0, attributesStart)
}

const isElementLabelledSpan = (op: string | undefined) =>
  op !== undefined && ELEMENT_LABELLED_SPAN_OPS.some(prefix => op.startsWith(prefix))

const isElementLabelledAttribute = (key: string) =>
  key === 'lcp.element' || key.startsWith('cls.source.')

export const maskSpan = <T extends SpanLike>(span: T): T => ({
  ...span,
  description:
    span.description !== undefined && isElementLabelledSpan(span.op)
      ? stripElementAttributes(span.description)
      : span.description,
  data: Object.fromEntries(
    Object.entries(span.data).map(([key, value]) => [
      key,
      typeof value === 'string' && isElementLabelledAttribute(key)
        ? stripElementAttributes(value)
        : value,
    ])
  ),
})

export const maskDynamicSamplingContext = (
  dsc: { transaction?: string },
  rootSpanOp: string | undefined
) => {
  if (dsc.transaction !== undefined && isElementLabelledSpan(rootSpanOp)) {
    dsc.transaction = stripElementAttributes(dsc.transaction)
  }
}

export const dropConsoleLogRecord = <T extends { attributes?: Record<string, unknown> }>(
  record: T
) => {
  const source = record.attributes?.['log.source']
  return typeof source === 'string' && source.startsWith('console.') ? null : record
}
